<template>
  <div class="sgc-shell">
    <div class="sgc-card">
      <div class="sgc-header">
        <div>
          <div class="sgc-header__title">Store Groups</div>
          <div class="sgc-header__sub">Manage group memberships for collaborative features</div>
        </div>
        <div class="sgc-header__actions">
          <hs-button variant="secondary" icon="log-in" @click="showJoinModal = true">Join Group</hs-button>
          <hs-button variant="primary" icon="plus" @click="showCreateModal = true">Create Group</hs-button>
        </div>
      </div>

      <div v-if="groups.length > 0 || searchQuery || roleFilter" class="sgc-filters">
        <hs-input v-model="searchQuery" icon="search" placeholder="Search by group name…" label="Search" />
        <hs-select
          v-model="roleFilter"
          label="Role"
          placeholder="All Roles"
          :options="roleOptions"
        />
        <div class="sgc-filters__clear">
          <hs-button variant="ghost" icon="x" @click="clearFilters">Clear</hs-button>
        </div>
      </div>

      <div v-if="loading" class="sgc-state">
        <span class="sgc-spinner" />
        Loading groups…
      </div>

      <hs-alert v-else-if="error" tone="danger" :message="error" class="sgc-alert">
        <template #default>
          {{ error }}
          <hs-button variant="ghost" size="sm" @click="loadGroups">Retry</hs-button>
        </template>
      </hs-alert>

      <hs-empty-state
        v-else-if="groups.length === 0 && !searchQuery && !roleFilter"
        icon="users"
        title="No groups yet"
        description="Create a group or join an existing one to collaborate with other stores."
      />

      <hs-empty-state
        v-else-if="groups.length === 0"
        icon="search"
        title="No results"
        description="No groups match your filters."
      />

      <div v-else class="sgc-table-shell">
        <table class="sgc-table">
          <thead>
            <tr>
              <th>Group</th>
              <th>Features</th>
              <th>Joined</th>
              <th class="sgc-table__actions-head">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="group in groups"
              :key="group.id"
              class="sgc-table__row"
              @click="openGroupDetails(group)"
            >
              <td>
                <div class="sgc-group-cell">
                  <div class="sgc-group-cell__mark">
                    {{ groupInitials(group.name) }}
                  </div>
                  <div class="sgc-group-cell__copy">
                    <div class="sgc-group-cell__name-row">
                      <span class="sgc-group-cell__name">{{ group.name }}</span>
                      <hs-badge :tone="getRoleTone(group.membership?.role)" size="sm">
                        {{ group.membership?.join_source || group.membership?.role || "MEMBER" }}
                      </hs-badge>
                    </div>
                    <p v-if="group.description" class="sgc-group-cell__desc">{{ group.description }}</p>
                    <p v-else class="sgc-group-cell__desc">No description provided.</p>
                  </div>
                </div>
              </td>
              <td>
                <div v-if="getGroupFeatures(group).length" class="sgc-feature-chips">
                  <hs-badge
                    v-for="feature in getGroupFeatures(group)"
                    :key="feature"
                    tone="info"
                    size="sm"
                  >
                    {{ feature }}
                  </hs-badge>
                </div>
                <span v-else class="sgc-table__muted">No sync features</span>
              </td>
              <td>
                <span class="sgc-table__muted">{{ formatDate(group.membership?.joined_at) }}</span>
              </td>
              <td @click.stop>
                <div class="sgc-table__actions">
                  <hs-button
                    v-if="canLeaveGroup(group)"
                    variant="ghost"
                    icon="log-out"
                    size="sm"
                    tone="danger"
                    @click="confirmLeaveGroup(group)"
                  >
                    Leave
                  </hs-button>
                  <hs-button
                    variant="secondary"
                    icon="eye"
                    size="sm"
                    @click="openGroupDetails(group)"
                  >
                    View
                  </hs-button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <hs-pagination
          v-if="pagination.total > 0"
          :current-page="pagination.page"
          :total-pages="pagination.pages"
          :total-items="pagination.total"
          :items-per-page="pagination.perPage"
          @previous-page="handlePreviousPage"
          @next-page="handleNextPage"
          @update:itemsPerPage="handlePerPageChange"
        />
      </div>
    </div>

    <create-group-modal
      v-if="showCreateModal"
      :store-id="storeId"
      @close="showCreateModal = false"
      @created="onGroupCreated"
    />

    <join-group-modal
      v-if="showJoinModal"
      :store-id="storeId"
      @close="showJoinModal = false"
      @joined="onGroupJoined"
    />

    <group-details-modal
      v-if="selectedGroup"
      :group="selectedGroup"
      :store-id="storeId"
      @close="selectedGroup = null"
      @refresh="onGroupUpdated"
    />

    <hs-confirm-dialog
      v-model="showLeaveConfirm"
      title="Leave Group"
      :message="groupToLeave ? `Are you sure you want to leave '${groupToLeave.name}'? You'll need a new invitation to rejoin.` : ''"
      confirm-text="Leave"
      cancel-text="Cancel"
      tone="danger"
      :loading="leaving"
      @confirm="leaveGroup"
    />
  </div>
</template>

<script>
import { ref, onMounted, watch, computed } from "vue";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import http from "@/helper/http";
import CreateGroupModal from "@/components/settings/CreateGroupModal.vue";
import JoinGroupModal from "@/components/settings/JoinGroupModal.vue";
import GroupDetailsModal from "@/components/settings/group/GroupDetailsModal.vue";
import { HsButton, HsInput, HsSelect, HsBadge, HsAlert, HsEmptyState, HsConfirmDialog, HsPagination } from "@/components/ui";

export default {
  name: "StoreGroupsCard",
  components: {
    CreateGroupModal,
    JoinGroupModal,
    GroupDetailsModal,
    HsButton,
    HsInput,
    HsSelect,
    HsBadge,
    HsAlert,
    HsEmptyState,
    HsConfirmDialog,
    HsPagination,
  },
  props: {
    storeId: { type: Number, required: true },
  },
  setup(props) {
    const store = useStore();
    const loading = ref(false);
    const error = ref("");
    const groups = ref([]);
    const selectedGroup = ref(null);
    const showCreateModal = ref(false);
    const showJoinModal = ref(false);
    const groupToLeave = ref(null);
    const showLeaveConfirm = ref(false);
    const leaving = ref(false);
    const searchQuery = ref("");
    const roleFilter = ref("");

    const pagination = ref({
      page: 1,
      perPage: 10,
      total: 0,
      pages: 0,
      hasNext: false,
      hasPrev: false,
    });

    const roleOptions = computed(() => [
      { label: "Owner", value: "OWNER" },
      { label: "Admin", value: "ADMIN" },
      { label: "Member", value: "MEMBER" },
    ]);

    const loadGroups = async () => {
      loading.value = true;
      error.value = "";
      try {
        const params = { page: pagination.value.page, per_page: pagination.value.perPage };
        if (searchQuery.value) { params.search_query = searchQuery.value; params.search_by = "name"; }
        if (roleFilter.value) params.role = roleFilter.value;

        const response = await http.get(`/v2/stores/${props.storeId}/groups`, { params });
        const data = response.data || {};
        groups.value = data.items || [];
        pagination.value = {
          page: data.page || 1,
          perPage: data.per_page || 10,
          total: data.total || 0,
          pages: data.pages || 0,
          hasNext: data.has_next || false,
          hasPrev: data.has_prev || false,
        };
      } catch (err) {
        console.error("Failed to load groups", err);
        error.value = err?.response?.data?.error?.message || "Unable to load groups.";
      } finally {
        loading.value = false;
      }
    };

    const handlePreviousPage = () => { if (pagination.value.hasPrev) { pagination.value.page -= 1; loadGroups(); } };
    const handleNextPage = () => { if (pagination.value.hasNext) { pagination.value.page += 1; loadGroups(); } };
    const handlePerPageChange = (perPage) => { pagination.value.perPage = perPage; pagination.value.page = 1; loadGroups(); };
    const clearFilters = () => { searchQuery.value = ""; roleFilter.value = ""; pagination.value.page = 1; };
    const openGroupDetails = (group) => { selectedGroup.value = group; };
    const onGroupCreated = async () => { showCreateModal.value = false; toast.success("Group created successfully"); await loadGroups(); };
    const onGroupJoined = async () => { showJoinModal.value = false; toast.success("Joined group successfully"); await loadGroups(); };
    const onGroupUpdated = async () => { await loadGroups(); };

    const confirmLeaveGroup = (group) => {
      groupToLeave.value = group;
      showLeaveConfirm.value = true;
    };

    const leaveGroup = async () => {
      if (!groupToLeave.value) return;
      leaving.value = true;
      try {
        await store.dispatch("groups/leaveGroup", { groupId: groupToLeave.value.id, storeId: props.storeId });
        toast.success("Left group successfully");
        showLeaveConfirm.value = false;
        groupToLeave.value = null;
        await loadGroups();
      } catch (err) {
        console.error("Failed to leave group", err);
        toast.error(err?.response?.data?.error?.message || "Unable to leave group.");
      } finally {
        leaving.value = false;
      }
    };

    const canLeaveGroup = (group) => group.membership?.role !== "OWNER";

    const getRoleTone = (role) => {
      const tones = { OWNER: "primary", ADMIN: "warning", MEMBER: "success" };
      return tones[role] || "neutral";
    };

    const getGroupFeatures = (group) => {
      const features = [];
      if (group.settings?.sync_products) features.push("Product Sync");
      if (group.settings?.sync_services) features.push("Service Sync");
      return features;
    };

    const formatDate = (dateString) => {
      if (!dateString) return "Unknown";
      return new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    };

    const groupInitials = (name = "") =>
      String(name || "G")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");

    watch([searchQuery, roleFilter], () => { pagination.value.page = 1; loadGroups(); });
    onMounted(() => { loadGroups(); });

    return {
      loading, error, groups, selectedGroup, showCreateModal, showJoinModal,
      groupToLeave, showLeaveConfirm, leaving, searchQuery, roleFilter,
      pagination, roleOptions,
      loadGroups, handlePreviousPage, handleNextPage, handlePerPageChange, clearFilters,
      openGroupDetails, onGroupCreated, onGroupJoined, onGroupUpdated,
      confirmLeaveGroup, leaveGroup, canLeaveGroup,
      getRoleTone, getGroupFeatures, formatDate, groupInitials,
    };
  },
};
</script>

<style scoped>
.sgc-shell {
  display: grid;
  gap: 1.25rem;
}

.sgc-card {
  background: color-mix(in srgb, var(--hs-surface) 92%, transparent);
  border: 1px solid var(--hs-border);
  border-radius: 14px;
  box-shadow: none;
  display: grid;
  gap: 1.25rem;
  padding: 1.5rem;
}

.sgc-header {
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: space-between;
}

.sgc-header__title {
  color: var(--hs-text);
  font-size: 1.05rem;
  font-weight: 950;
  line-height: 1.2;
}

.sgc-header__sub {
  color: var(--hs-muted);
  font-size: 0.8rem;
  margin-top: 0.2rem;
}

.sgc-header__actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

.sgc-filters {
  align-items: flex-end;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 1fr 1fr auto;
}

.sgc-filters__clear {
  padding-bottom: 0.1rem;
}

@media (max-width: 640px) {
  .sgc-filters {
    grid-template-columns: 1fr;
  }
}

.sgc-state {
  align-items: center;
  color: var(--hs-muted);
  display: flex;
  font-size: 0.875rem;
  gap: 0.65rem;
  justify-content: center;
  padding: 3rem 0;
}

.sgc-spinner {
  animation: spin 0.8s linear infinite;
  border: 2px solid color-mix(in srgb, var(--hs-primary) 28%, transparent);
  border-radius: 50%;
  border-top-color: var(--hs-primary);
  display: inline-block;
  height: 1.15rem;
  width: 1.15rem;
}

@keyframes spin { to { transform: rotate(360deg); } }

.sgc-alert {
  margin: 0;
}

.sgc-table-shell {
  border: 1px solid var(--hs-border);
  border-radius: 14px;
  overflow-x: auto;
}

.sgc-table {
  border-collapse: collapse;
  min-width: 760px;
  width: 100%;
}

.sgc-table thead {
  background: color-mix(in srgb, var(--hs-bg-soft) 55%, transparent);
}

.sgc-table th {
  color: var(--hs-muted);
  font-size: 0.72rem;
  font-weight: 850;
  letter-spacing: 0.06em;
  padding: 0.85rem 1rem;
  text-align: left;
  text-transform: uppercase;
}

.sgc-table td {
  border-top: 1px solid var(--hs-border);
  padding: 0.95rem 1rem;
  vertical-align: middle;
}

.sgc-table__row {
  cursor: pointer;
  transition: background 0.18s ease;
}

.sgc-table__row:hover {
  background: color-mix(in srgb, var(--hs-primary) 4%, transparent);
}

.sgc-table__actions-head {
  text-align: right;
}

.sgc-table__actions {
  align-items: center;
  display: flex;
  gap: 0.45rem;
  justify-content: flex-end;
}

.sgc-table__muted {
  color: var(--hs-muted);
  font-size: 0.8rem;
  font-weight: 700;
}

.sgc-group-cell {
  align-items: center;
  display: flex;
  gap: 0.8rem;
  min-width: 0;
}

.sgc-group-cell__mark {
  align-items: center;
  background: color-mix(in srgb, var(--hs-primary) 12%, transparent);
  border: 1px solid color-mix(in srgb, var(--hs-primary) 18%, transparent);
  border-radius: 12px;
  color: var(--hs-primary);
  display: inline-flex;
  flex-shrink: 0;
  font-size: 0.78rem;
  font-weight: 950;
  height: 2.45rem;
  justify-content: center;
  width: 2.45rem;
}

.sgc-group-cell__copy {
  min-width: 0;
}

.sgc-group-cell__name-row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.sgc-group-cell__name {
  color: var(--hs-text);
  font-size: 0.92rem;
  font-weight: 850;
}

.sgc-group-cell__desc {
  color: var(--hs-muted);
  font-size: 0.8rem;
  margin-top: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sgc-feature-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}
</style>
